/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Information, Launch } from '@carbon/react/icons';
import {
  Button,
  ListItem,
  Link,
  OrderedList,
  TextInput,
  TooltipIcon
} from 'carbon-components-react';
import { useFormContext } from 'react-hook-form';
import { useIntl, FormattedMessage } from 'react-intl';
import ReactPlayer from 'react-player';

import { generateDocsUrl } from '../../../../ia/util/docs';
import { useChannelHelpers } from '../../../hooks/useChannelHelpers';
import { FIELDS } from '../schema';
import { ConnectStepMessages as messages } from './messages';

import './ConnectStepContent.scss';

interface ConnectStepContentProps {
  isLive: boolean;
  readOnly: boolean;
  tokenUrl: string;
}
export const ConnectStepContent = ({
  isLive,
  readOnly,
  tokenUrl
}: ConnectStepContentProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { useValidSegmentKey } = useChannelHelpers(formMethods);

  return (
    <div className="ConnectStepContent">
      <div className="ConnectStepContent__list">
        <OrderedList className="TabContent__subtitle">
          <ListItem>
            {!isLive ? (
              <>
                <div className="ConnectStep__step">
                  <FormattedMessage {...messages.stepOne} />
                  <Link
                    className="ConnectStep__step-one-link"
                    href="https://app.segment.com/login"
                    target="_blank"
                    rel="noopener noreferrer">
                    <FormattedMessage {...messages.stepOneLink} />
                  </Link>
                </div>
                <Button
                  className="ConnectStep__signUp"
                  kind="primary"
                  href={tokenUrl}
                  disabled={readOnly}
                  renderIcon={Launch}
                  target="_blank"
                  rel="noopener noreferrer">
                  <FormattedMessage {...messages.signUpAccount} />
                </Button>
              </>
            ) : (
              <div className="ConnectStep__step">
                <FormattedMessage {...messages.navigateToSegment} />
                <Link
                  className="ConnectStep__step-one-link"
                  href="https://app.segment.com/login"
                  target="_blank"
                  rel="noopener noreferrer">
                  <FormattedMessage {...messages.segmentWorkspace} />
                </Link>
                <TooltipIcon
                  className="ConnectStep__info-icon"
                  direction="top"
                  tooltipText={t(messages.tooltip)}>
                  <Information size={16} />
                </TooltipIcon>
              </div>
            )}
          </ListItem>
          <ListItem>
            <div className="ConnectStep__step">
              <FormattedMessage {...messages.stepTwo} />
            </div>
            <TextInput
              className="ConnectStep__segment-key-input"
              disabled={readOnly}
              id="ConnectStep__key"
              labelText={'Segment key'}
              {...useValidSegmentKey(FIELDS.SEGMENT_WRITE_KEY, readOnly)}
            />
          </ListItem>
        </OrderedList>
      </div>
      <div className="ConnectStepContent__video">
        <ReactPlayer
          controls={true}
          height="280px"
          url="https://player.vimeo.com/video/729663077?h=47f028ed05&badge=0&autopause=0&player_id=0&app_id=58479"
          width="100%"
        />
        <div className="ConnectStepContent__video-description">
          <FormattedMessage {...messages.videoDescription} />
          <Link
            className="ConnectStep__step-one-link"
            href={generateDocsUrl(
              '/watson-assistant?topic=watson-assistant-segment-add'
            )}
            target="_blank"
            rel="noopener noreferrer">
            <FormattedMessage {...messages.videoDescriptionLink} />
          </Link>
        </div>
      </div>
    </div>
  );
};
